/**
 * providers/AuthProvider.tsx
 * Authentication context provider
 *
 * Manages global authentication state and provides it to all components via context.
 * This ensures session initialization and auth operations run only once per app instance.
 *
 * NOTE:
 * This file contains all the authentication logic previously in src/hooks/useAuth.ts.
 *
 * TODOs left to implement in this file:
 * - Advanced session security (device fingerprinting, IP tracking, geolocation monitoring)
 * - Authentication rate limiting (brute force protection, CAPTCHA integration)
 * - Offline/online state management (network connectivity handling)
 * - Server-side session management (WebSocket/SSE, cross-device revocation)
 * - Authentication analytics integration (login patterns, session duration, security events)
 * - Production email change flows (backend validation, email availability checks)
 *
*/

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { AuthContext } from '@/contexts/AuthContext';
import { authService, passwordService, emailChangeService, profileService } from '@/services/auth';
import type { LoginResult } from '@/services/auth';
import { MFALoginChallengeModal } from '@/components/shared/auth';
import type { Factor } from '@/types/mfa';
import { AuthErrorCode } from '@/types/auth';
import type {
  AuthState,
  UseAuthReturn,
  LoginRequest,
  RegisterRequest,
  PasswordResetRequest,
  PasswordResetConfirmRequest,
  ChangePasswordRequest,
  EmailChangeRequest,
  EmailVerificationRequest,
  AuthSuccessResponse,
  AuthErrorResponse,
  AuthOperationResponse,
  User
} from '@/types/auth';
import { redirectAfterLogout } from '@/utils/authRedirect';

/**
 * Initial authentication state
 */
const initialAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true, // Start as loading while we check for existing session
  sessionToken: null,
  sessionExpiresAt: null
};

/**
 * AuthProvider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Provides authentication state and operations to all child components.
 * Automatically manages session restoration, validation, and cleanup.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // ===== STATE MANAGEMENT =====

  const [authState, setAuthState] = useState<AuthState>(initialAuthState);

  // MFA challenge state (for login flow)
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string;
    factorId: string;
    expiresAt: number;
    availableFactors: Factor[];
    pendingSession: {
      user: User;
      session: {
        access_token: string;
        expires_at?: number;
      };
    };
  } | null>(null);

  // ReAuth in progress guard (prevents session rotation during ReAuthGate)
  const reauthInProgress = useRef(false);

  // Login in progress guard (prevents SIGNED_IN event from racing with MFA check)
  const loginInProgress = useRef(false);

  // Logout in progress guard (prevents duplicate clearAuthState() from SIGNED_OUT + storage events)
  const logoutInProgress = useRef(false);

  // User role ref (keeps clearAuthState stable for event listeners)
  // Updated via useEffect whenever user role changes, prevents re-binding subscriptions
  const userRoleRef = useRef<'user' | 'admin'>('user');

  // Force-logout Realtime channel ref
  // Holds the active broadcast channel for cross-device session revocation.
  // Set when authenticated, cleared on sign-out or unmount.
  const forceLogoutChannelRef = useRef<RealtimeChannel | null>(null);

  // Intended logout mode ref (H-01 fix)
  // Set by logoutEverywhere() before signOut fires, so the SIGNED_OUT event handler
  // uses the correct redirect mode instead of the default 'local'.
  const logoutModeRef = useRef<'local' | 'global' | 'revoked'>('local');

  // TODO: [CONTRACTOR] Implement advanced session security - device fingerprinting, IP tracking, geolocation monitoring, and anomaly detection for suspicious login patterns

  // ===== HELPER FUNCTIONS =====
  // buildUserFromSession moved to AuthService

  // ===== SESSION MANAGEMENT =====

  /**
   * Initialize authentication state by checking for existing session
   * Delegates to AuthService and applies state snapshot
   */
  const initializeAuth = useCallback(async () => {
    try {
      const result = await authService.initializeAuth();

      if (result.status === 'unauthenticated') {
        setAuthState({
          ...initialAuthState,
          isLoading: false
        });
        return;
      }

      // Apply authenticated state snapshot from AuthService
      setAuthState({
        isAuthenticated: true,
        user: result.user,
        isLoading: false,
        sessionToken: result.session.access_token,
        sessionExpiresAt: result.session.expires_at
          ? new Date(result.session.expires_at * 1000).toISOString()
          : null
      });
    } catch (error) {
      console.error('❌ AuthProvider: Session initialization error:', error);
      setAuthState({
        ...initialAuthState,
        isLoading: false
      });
    }
  }, []);

  /**
   * Session refresh is handled automatically by Supabase Auth
   * via the onAuthStateChange listener (see useEffect below)
   */

  /**
   * Clear authentication state (internal logout)
   * Guard: Only execute once using logoutInProgress ref
   *
   * @param mode - Redirect mode passed to redirectAfterLogout ('local' | 'global' | 'revoked')
   *               Defaults to 'local'. Pass 'global' when revoking all sessions.
   */
  const clearAuthState = useCallback(async (mode: 'local' | 'global' | 'revoked' = 'local') => {
    // Guard: If logout already in progress, skip to prevent duplicate cleanup
    if (logoutInProgress.current) {
      console.log('🔄 AuthProvider: Logout already in progress, skipping clearAuthState');
      return;
    }

    logoutInProgress.current = true;
    console.log('🧹 AuthProvider: Clearing authentication state');
    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      sessionToken: null,
      sessionExpiresAt: null
    });

    // Always purge Supabase auth tokens from localStorage.
    // Covers all logout paths — including cross-device Realtime broadcast where
    // supabase.auth.signOut() was never called on this device, leaving tokens
    // intact and allowing initializeAuth() to restore the session on reload.
    // For paths where signOut() already cleared them (logout, SIGNED_OUT event),
    // this is a safe no-op.
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.includes('-auth-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log(`🔐 AuthProvider: Removed Supabase auth key: ${key}`);
      });
    } catch (storageError) {
      console.error('⚠️ AuthProvider: Failed to clear Supabase auth from localStorage:', storageError);
      // Non-blocking — redirect still proceeds
    }

    // Redirect to appropriate login page using centralized utility
    // Uses userRoleRef for stable reference (prevents re-binding event listeners)
    redirectAfterLogout({
      mode,
      userRole: userRoleRef.current
    });
  }, []);

  // ===== AUTHENTICATION OPERATIONS =====

  // TODO: [CONTRACTOR] Implement authentication rate limiting - prevent brute force attacks with exponential backoff, account lockout, and CAPTCHA integration

  /**
   * Authenticate user with email and password
   * Delegates to AuthService and handles MFA UI state
   */
  const login = useCallback(async (request: LoginRequest): Promise<AuthSuccessResponse | AuthErrorResponse> => {
    // Set login in progress flag to prevent SIGNED_IN event from racing with MFA check
    loginInProgress.current = true;

    setAuthState(prevState => ({
      ...prevState,
      isLoading: true
    }));

    try {
      // Delegate to AuthService
      const result: LoginResult = await authService.login(request);

      if (result.type === 'error') {
        setAuthState(prevState => ({
          ...prevState,
          isLoading: false
        }));

        return {
          success: false,
          error: result.error,
          message: result.message
        };
      }

      if (result.type === 'mfa_required') {
        // Set MFA challenge state (don't complete login yet)
        setMfaChallenge({
          challengeId: result.challenge.challengeId,
          factorId: result.challenge.factorId,
          expiresAt: result.challenge.expiresAt,
          availableFactors: result.challenge.availableFactors,
          pendingSession: result.challenge.pendingSession
        });

        // Clear loading state for MFA modal
        setAuthState(prevState => ({
          ...prevState,
          isLoading: false
        }));

        // Return special response indicating MFA required
        return {
          success: false,
          error: 'MFA_REQUIRED' as AuthErrorCode,
          message: 'Multi-factor authentication required'
        };
      }

      // Success - apply state
      setAuthState({
        isAuthenticated: true,
        user: result.sessionUser,
        isLoading: false,
        sessionToken: result.session.access_token,
        sessionExpiresAt: result.session.expires_at
          ? new Date(result.session.expires_at * 1000).toISOString()
          : null
      });

      return {
        success: true,
        user: result.sessionUser,
        sessionToken: result.session.access_token,
        expiresAt: new Date((result.session.expires_at || 0) * 1000).toISOString()
      };
    } catch (error) {
      console.error('❌ AuthProvider: Login error:', error);
      setAuthState(prevState => ({
        ...prevState,
        isLoading: false
      }));

      return {
        success: false,
        error: 'NETWORK_ERROR' as AuthErrorCode,
        message: 'An unexpected error occurred during login'
      };
    } finally {
      // Clear login in progress flag
      loginInProgress.current = false;
    }
  }, []);

  /**
   * Verify MFA code and complete login
   * Called by MFALoginChallengeModal after user enters TOTP code
   * Delegates to AuthService and applies state
   */
  const verifyMFALogin = useCallback(
    async (code: string, usedFactorId: string): Promise<void> => {
      if (!mfaChallenge) {
        throw new Error('No MFA challenge in progress');
      }

      // Delegate to AuthService
      const result = await authService.verifyMFALogin(
        code,
        usedFactorId,
        mfaChallenge.challengeId,
        mfaChallenge.pendingSession
      );

      // Apply state from successful verification
      setAuthState({
        isAuthenticated: true,
        user: result.user,
        isLoading: false,
        sessionToken: result.session.access_token,
        sessionExpiresAt: result.session.expires_at
          ? new Date(result.session.expires_at * 1000).toISOString()
          : null
      });

      // Clear MFA challenge state
      setMfaChallenge(null);
    },
    [mfaChallenge]
  );

  /**
   * Cancel MFA challenge and return to login
   */
  const cancelMFAChallenge = useCallback(() => {
    console.log('🔐 AuthProvider: MFA challenge cancelled');

    // Clear MFA challenge state
    setMfaChallenge(null);

    // Sign out the pending session
    supabase.auth.signOut();
  }, []);

  /**
   * Low-level signup primitive - creates Supabase auth.users record
   * Delegates to AuthService
   */
  const signup = useCallback(
    async (credentials: {
      email: string;
      password: string;
      options?: {
        emailRedirectTo?: string;
        data?: Record<string, unknown>;
        captchaToken?: string;
      };
    }): Promise<{ success: true; userId: string } | { success: false; error: AuthErrorCode; message: string }> => {
      // Delegate to AuthService
      return authService.signup(credentials);
    },
    []
  );

  /**
   * High-level registration with business logic
   * Delegates to AuthService
   */
  const register = useCallback(async (request: RegisterRequest): Promise<AuthSuccessResponse | AuthErrorResponse> => {
    // Delegate to AuthService
    return authService.register(request);
  }, []);

  /**
   * Log out current user
   *
   * Implementation Note: Always calls clearAuthState() after logout to ensure cleanup.
   * This handles both online and offline scenarios:
   * - Online: SIGNED_OUT event fires first, sets guard, manual call is no-op
   * - Offline: SIGNED_OUT event doesn't fire, manual call performs cleanup
   *
   * localStorage purging is handled inside clearAuthState() — covers all logout
   * paths including cross-device Realtime broadcast where signOut() was never called.
   *
   * clearAuthState() is idempotent (has internal guard) so multiple callers are safe.
   * clearAuthState() now owns localStorage purging for all logout paths.
   */
  const logout = useCallback(async (): Promise<void> => {
    setAuthState(prevState => ({
      ...prevState,
      isLoading: true
    }));

    try {
      // Delegate to AuthService (triggers SIGNED_OUT event when online)
      await authService.logout();
    } catch (error) {
      console.error('❌ AuthProvider: Logout error:', error);
      // Continue with cleanup even if signOut() throws
    }

    // Always call clearAuthState to ensure cleanup
    // (idempotent - has internal guard to prevent duplicate execution)
    await clearAuthState();
  }, [clearAuthState]);

  /**
   * Revoke all sessions for the current user across all devices
   *
   * Calls AuthService which invokes revoke-sessions-and-audit Edge Function
   * then signs out globally. Redirects to login with mode: 'global'.
   */
  const logoutEverywhere = useCallback(async (): Promise<void> => {
    if (!authState.user) {
      console.warn('⚠️ AuthProvider: logoutEverywhere called with no authenticated user');
      return;
    }

    // Set intended mode before anything fires so the SIGNED_OUT handler
    // (triggered by signOut below) uses the correct redirect mode.
    logoutModeRef.current = 'global';

    setAuthState(prevState => ({
      ...prevState,
      isLoading: true
    }));

    // Broadcast FIRST — channel is still alive and user is still authenticated.
    // Must happen before signOut(), which triggers SIGNED_OUT → clearAuthState() →
    // isAuthenticated: false → Realtime useEffect cleanup tears the channel down.
    try {
      if (forceLogoutChannelRef.current) {
        await forceLogoutChannelRef.current.send({
          type: 'broadcast',
          event: 'FORCE_LOGOUT',
          payload: {}
        });
        console.log('📡 AuthProvider: Force logout broadcast sent');
      }
    } catch (broadcastError) {
      console.error('❌ AuthProvider: Failed to broadcast force logout:', broadcastError);
      // Non-blocking — Layer 1 (token revocation) covers offline devices on reconnect
    }

    // Server-side revocation: Edge Function audit + signOut({ scope: 'global' }).
    // On failure: restore isLoading (prevents ProtectedRoute loading-screen lockup),
    // reset logoutModeRef (prevents stale 'global' mode on next SIGNED_OUT), rethrow
    // so SecurityCard can show the error toast.
    // On success: logoutModeRef stays 'global' — the SIGNED_OUT handler or the
    // safety-net clearAuthState('global') below will consume it, then page redirects.
    try {
      await authService.logoutEverywhere(authState.user.id, navigator.userAgent);
    } catch (error) {
      logoutModeRef.current = 'local';
      setAuthState(prevState => ({ ...prevState, isLoading: false }));
      throw error;
    }

    // Safety net: if SIGNED_OUT event has not yet called clearAuthState (async timing),
    // this ensures cleanup runs. logoutInProgress guard makes it a no-op if already done.
    await clearAuthState('global');
  }, [authState.user, clearAuthState]);

  // ===== PASSWORD OPERATIONS =====
  // Delegate to PasswordService

  /**
   * Request password reset email
   * Delegates to PasswordService
   */
  const requestPasswordReset = useCallback(async (request: PasswordResetRequest): Promise<AuthOperationResponse> => {
    return passwordService.requestPasswordReset(request);
  }, []);

  /**
   * Confirm password reset with token
   * Delegates to PasswordService
   */
  const confirmPasswordReset = useCallback(async (request: PasswordResetConfirmRequest): Promise<AuthOperationResponse> => {
    return passwordService.confirmPasswordReset(request);
  }, []);

  /**
   * Change password for authenticated user
   * Delegates to PasswordService
   */
  const changePassword = useCallback(
    async (request: ChangePasswordRequest): Promise<AuthOperationResponse> => {
      if (!authState.user) {
        return {
          success: false,
          message: 'No authenticated user found'
        };
      }

      // Delegate to PasswordService with user context
      return passwordService.changePassword({
        ...request,
        userId: authState.user.id,
        userEmail: authState.user.email
      });
    },
    [authState.user]
  );

  /**
   * Update display name for authenticated user
   * Delegates to ProfileService and updates React state
   */
  const updateDisplayName = useCallback(
    async (newDisplayName: string): Promise<AuthOperationResponse> => {
      if (!authState.user) {
        return {
          success: false,
          message: 'No authenticated user found'
        };
      }

      // Delegate to ProfileService
      const result = await profileService.updateDisplayName(newDisplayName, authState.user.id);

      if (result.success && result.displayName) {
        // Update React state with new display name
        const displayName = result.displayName; // Capture for type narrowing
        setAuthState(prev => ({
          ...prev,
          user: prev.user ? { ...prev.user, displayName } : null
        }));
      }

      return result;
    },
    [authState.user]
  );

  // ===== EMAIL CHANGE OPERATIONS =====

  /**
   * Request email address change for authenticated user
   */
  const requestEmailChange = useCallback(async (request: EmailChangeRequest): Promise<AuthOperationResponse> => {
    if (!authState.user) {
      return {
        success: false,
        message: 'No authenticated user found'
      };
    }

    // Delegate to EmailChangeService
    return emailChangeService.requestEmailChange(
      request,
      authState.user.id,
      authState.user.email,
      authState.user.displayName
    );
  }, [authState.user]);

  /**
   * Verify email change with token
   */
  const verifyEmailChange = useCallback(async (request: EmailVerificationRequest): Promise<AuthOperationResponse> => {
    // Delegate to EmailChangeService
    const result = await emailChangeService.verifyEmailChange(request);

    // Update React state if verification succeeded and we have a new email
    if (result.success && result.newEmail && authState.user) {
      const newEmail = result.newEmail; // Capture for type narrowing
      setAuthState(prev => ({
        ...prev,
        user: prev.user ? {
          ...prev.user,
          email: newEmail,
          emailVerified: true
        } : null
      }));
    }

    return result;
  }, [authState.user]);

  /**
   * Get pending email change information
   */
  const getPendingEmailChange = useCallback(async (): Promise<{ newEmail: string; createdAt: string } | null> => {
    if (!authState.user) {
      return null;
    }

    // Delegate to EmailChangeService
    return emailChangeService.getPendingEmailChange(authState.user.id);
  }, [authState.user]);

  /**
   * Cancel pending email change
   */
  const cancelEmailChange = useCallback(async (): Promise<AuthOperationResponse> => {
    if (!authState.user) {
      return {
        success: false,
        message: 'No authenticated user found'
      };
    }

    // Delegate to EmailChangeService
    return emailChangeService.cancelEmailChange(authState.user.id);
  }, [authState.user]);

  /**
   * Resend email verification
   */
  const resendEmailVerification = useCallback(async (): Promise<AuthOperationResponse> => {
    if (!authState.user) {
      return {
        success: false,
        message: 'No authenticated user found'
      };
    }

    // Delegate to EmailChangeService
    return emailChangeService.resendEmailVerification(
      authState.user.id,
      authState.user.email,
      authState.user.displayName
    );
  }, [authState.user]);

  // ===== LIFECYCLE EFFECTS =====

  // TODO: [CONTRACTOR] Implement offline/online state management - handle network connectivity changes, queue operations during offline, and sync on reconnection

  /**
   * Sync user role ref whenever user role changes
   * Keeps clearAuthState referentially stable for event listeners
   */
  useEffect(() => {
    userRoleRef.current = authState.user?.role === 'admin' ? 'admin' : 'user';
  }, [authState.user?.role]);

  /**
   * Subscribe to force-logout Realtime broadcast channel when authenticated.
   *
   * On logoutEverywhere, the initiating device broadcasts FORCE_LOGOUT to this
   * user-scoped channel. All other connected devices receive it and immediately
   * call clearAuthState() — achieving true cross-device logout without waiting
   * for JWT expiry (~1 hour).
   *
   * The initiating device also receives its own broadcast, but the logoutInProgress
   * guard inside clearAuthState() makes it a safe no-op.
   *
   * Lifecycle:
   * - Subscribe when isAuthenticated becomes true
   * - Unsubscribe (cleanup) when isAuthenticated becomes false or userId changes
   */
  // Narrow to userId (string | undefined) so the effect only re-runs when the
  // authenticated user ID changes — not on any user object reconstruction.
  const userId = authState.user?.id;

  useEffect(() => {
    if (!authState.isAuthenticated || !userId) {
      return;
    }

    const channelName = `force-logout:${userId}`;

    const channel = supabase.channel(channelName);
    forceLogoutChannelRef.current = channel;

    channel
      .on('broadcast', { event: 'FORCE_LOGOUT' }, () => {
        console.log('📡 AuthProvider: Force logout broadcast received — signing out');
        // clearAuthState is idempotent: logoutInProgress guard prevents double execution
        // on the initiating device that also receives its own broadcast
        void clearAuthState('global');
      })
      .subscribe((status) => {
        console.log(`📡 AuthProvider: Force-logout channel [${channelName}] status: ${status}`);
      });

    return () => {
      forceLogoutChannelRef.current = null;
      void supabase.removeChannel(channel);
    };
  }, [authState.isAuthenticated, userId, clearAuthState]);

  /**
   * Initialize authentication state on mount
   */
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /**
   * Listen for Supabase auth state changes
   * Handles automatic session refresh, sign out, etc.
   */
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 useAuth: Auth state changed:', event);

        // Guard: Ignore auth state changes during ReAuthGate flow
        // This prevents session rotation when using signInWithPassword for credential verification
        if (reauthInProgress.current) {
          console.log('🔐 useAuth: Ignoring auth state change during reauth flow');
          return;
        }

        // Guard: Ignore SIGNED_IN events during login flow
        // This prevents race condition between SIGNED_IN event and login()'s MFA check
        if (loginInProgress.current && event === 'SIGNED_IN') {
          console.log('🔐 useAuth: Ignoring SIGNED_IN event during login flow (MFA check in progress)');
          return;
        }

        if (event === 'SIGNED_OUT') {
          // Use logoutModeRef so logoutEverywhere()'s 'global' mode is preserved.
          // For all other logout paths, logoutModeRef defaults to 'local'.
          await clearAuthState(logoutModeRef.current);
        } else if (event === 'TOKEN_REFRESHED' && session) {
          // Session refreshed, update token
          setAuthState(prevState => ({
            ...prevState,
            sessionToken: session.access_token,
            sessionExpiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null
          }));
        } else if (event === 'SIGNED_IN' && session) {
          // Handle sign-in from session restoration or cross-tab login
          console.log('✅ useAuth: User signed in via auth state change');

          // Only update state if we don't already have user data
          // (login/activation functions handle their own state updates)
          if (!authState.user) {
            console.log('🔐 useAuth: Session restored, checking AAL level...');

            // Decode JWT token to check AAL level (faster and more reliable than API call)
            let aalLevel: string | null = null;
            try {
              const accessToken = session.access_token;
              if (accessToken) {
                // Decode JWT payload (base64 decode the middle part)
                const parts = accessToken.split('.');
                if (parts[1]) {
                  const payload = JSON.parse(atob(parts[1]));
                  aalLevel = payload.aal || null;
                  console.log('🔐 useAuth: Current AAL level from JWT:', aalLevel);
                }
              }
            } catch (error) {
              console.error('❌ useAuth: Failed to decode JWT token:', error);
              // Fall back to null
            }

            // If AAL2, proceed with normal login
            if (aalLevel === 'aal2') {
              console.log('✅ useAuth: Session is AAL2, building user from session data...');

              // CRITICAL: Database queries hang on page refresh even with AAL2 JWT token.
              // This appears to be a localhost issue or timing issue with Supabase backend.
              // Solution: Build User object directly from session data instead of querying DB.
              // The session already contains all critical user data we need.

              const user = authService.buildUserFromSession(session);

              // Update auth state
              setAuthState({
                isAuthenticated: true,
                user,
                isLoading: false,
                sessionToken: session.access_token,
                sessionExpiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null
              });

              console.log('✅ useAuth: Auth state updated with session data for', user.email);
              return;
            }

            // Session is AAL1 - build user from session data (same as AAL2)
            console.log('🔐 useAuth: Session is AAL1, building user from session data...');

            // CRITICAL: Do NOT call listFactors() or query database for AAL1 sessions.
            // These operations can hang/timeout, causing infinite spinner.
            // Solution: Build User from session data only (same as AAL2 path).
            // Route guards will enforce AAL2 requirement for admin access separately.

            const user = authService.buildUserFromSession(session);

            // Update auth state - user is authenticated but may need MFA verification
            // for admin access (enforced by route guards, not here)
            setAuthState({
              isAuthenticated: true,
              user,
              isLoading: false,
              sessionToken: session.access_token,
              sessionExpiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null
            });

            console.log('✅ useAuth: Auth state updated with session data for', user.email);
          }
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
    // Note: authState.user is intentionally NOT in dependencies - we want ONE subscription
    // that persists for the lifetime of the component. The callback reads fresh authState
    // from the component scope on each auth event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAuthState]);

  /**
   * Listen for cross-tab logout synchronization
   * Supabase Auth automatically syncs session changes across tabs
   * This is a backup mechanism in case the auth state listener doesn't fire
   * TODO: [CONTRACTOR] Implement server-side session management - WebSocket/SSE for real-time session updates, cross-device revocation, session fingerprinting, and suspicious activity detection
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Supabase uses 'sb-<project-ref>-auth-token' key
      // If it's cleared, sync logout
      if (e.key?.startsWith('sb-') && e.key?.endsWith('-auth-token') && !e.newValue && authState.isAuthenticated) {
        console.log('🔄 useAuth: Logout detected in another tab, synchronizing logout');
        // clearAuthState() has internal guard to prevent duplicate cleanup
        clearAuthState();
      }
    };

    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [authState.isAuthenticated, clearAuthState]);

  /**
   * Session refresh is handled automatically by Supabase Auth
   * The onAuthStateChange listener will receive TOKEN_REFRESHED events
   * No manual timer needed
   */

  // ===== CONTEXT VALUE =====

  // TODO: [CONTRACTOR] Implement authentication analytics integration - track login patterns, session duration, device usage, security events, and user behavior metrics

  const contextValue: UseAuthReturn = {
    // Current state
    authState,

    // Authentication operations
    login,
    signup,
    register,
    logout,
    logoutEverywhere,

    // Password operations
    requestPasswordReset,
    confirmPasswordReset,
    changePassword,

    // Profile operations
    updateDisplayName,

    // Email change operations
    requestEmailChange,
    verifyEmailChange,
    getPendingEmailChange,
    cancelEmailChange,
    resendEmailVerification,

    // Session operations
    // refreshSession handled automatically by Supabase
    clearAuthState,

    // Convenience properties
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    user: authState.user
  };

  // ===== RENDER =====

  // Handle MFA verification from modal
  const handleMFAVerify = useCallback(async (code: string, factorId: string): Promise<void> => {
    await verifyMFALogin(code, factorId);
  }, [verifyMFALogin]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}

      {/* MFA Login Challenge Modal */}
      {mfaChallenge && (
        <MFALoginChallengeModal
          isOpen={true}
          challengeId={mfaChallenge.challengeId}
          factorId={mfaChallenge.factorId}
          expiresAt={mfaChallenge.expiresAt}
          availableFactors={mfaChallenge.availableFactors}
          onVerify={handleMFAVerify}
          onCancel={cancelMFAChallenge}
        />
      )}
    </AuthContext.Provider>
  );
};
